import { cva, type VariantProps } from 'class-variance-authority'
import { type LucideIcon, RefreshCw } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { IconCircle } from '../atoms/IconCircle'
import { StatusDot } from '../atoms/StatusDot'
import { formatRelative } from './FreshnessBanner'

/**
 * MVManagementCard — generic "group rollup card with bulk action" pattern,
 * built first for the Phase-1 Report Builder Settings page (3 cards, one
 * per Materialized View tier).
 *
 * Each card surfaces:
 *   - Tier title + optional subtitle (e.g. "Tier 1" / "Dashboard KPIs")
 *   - Member count + last-refresh time of the OLDEST member in the group
 *   - Status rollup: idle | running | partial-failed | all-failed | cold
 *   - Optional "Refresh tier" CTA + spinner state
 *
 * Generic enough to be reused for any "category card with bulk action"
 * surface — sister to StatCard but action-oriented rather than KPI-only.
 *
 * Composes: Button (atom), StatusDot (atom), IconCircle (atom), Icon (atom).
 */

export type MVCardStatus =
  | 'idle' // every member is idle/succeeded
  | 'running' // at least one member is currently refreshing
  | 'partial-failed' // some members failed, others succeeded
  | 'all-failed' // every member failed
  | 'cold' // no member has ever refreshed (fresh tenant / fresh deploy)

export const mvManagementCardVariants = cva(
  'rounded-lg bg-card border p-4 flex flex-col gap-3 transition-colors',
  {
    variants: {
      tone: {
        default: 'border-ia-border hover:border-ia-border-strong',
        success: 'border-ia-success/30 hover:border-ia-success/50',
        warning: 'border-ia-warning/30 hover:border-ia-warning/50',
        error: 'border-ia-error/30 hover:border-ia-error/50',
        info: 'border-ia-info/30 hover:border-ia-info/50',
      },
    },
    defaultVariants: { tone: 'default' },
  },
)

const STATUS_TO_DOT: Record<MVCardStatus, React.ComponentProps<typeof StatusDot>['status']> = {
  idle: 'active',
  running: 'pending',
  'partial-failed': 'pending',
  'all-failed': 'error',
  cold: 'inactive',
}

const STATUS_LABELS: Record<MVCardStatus, string> = {
  idle: 'Idle',
  running: 'Refreshing…',
  'partial-failed': 'Some failed',
  'all-failed': 'All failed',
  cold: 'Never refreshed',
}

export interface MVManagementCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof mvManagementCardVariants> {
  /** e.g. "Tier 1" — primary heading. */
  title: React.ReactNode
  /** e.g. "Dashboard KPIs" — small caption above the title. */
  subtitle?: React.ReactNode
  /** Optional accent icon shown top-right via IconCircle. */
  icon?: LucideIcon
  /** Total members in this group. */
  mvCount: number
  /** ISO 8601 string OR Date OR null. Drives the relative-time display. */
  lastRefreshedAt?: string | Date | null
  /** Status rollup for the group. Default 'idle'. */
  status?: MVCardStatus
  /** When status='partial-failed', surface "N of M failed". */
  failedCount?: number
  /**
   * Override the relative-time computation. Useful in stories + tests
   * where Date.now() would yield non-deterministic output.
   */
  nowFn?: () => Date
  /** Click handler for the refresh button. Button hidden when omitted. */
  onRefresh?: () => void | Promise<void>
  /** Disables the refresh button + shows spinner. */
  refreshing?: boolean
  /** Override the default "Refresh" button label. */
  refreshLabel?: string
}

export const MVManagementCard = React.forwardRef<HTMLDivElement, MVManagementCardProps>(
  (
    {
      className,
      tone,
      title,
      subtitle,
      icon,
      mvCount,
      lastRefreshedAt,
      status = 'idle',
      failedCount,
      nowFn,
      onRefresh,
      refreshing = false,
      refreshLabel = 'Refresh',
      ...props
    },
    ref,
  ) => {
    const now = nowFn ? nowFn() : new Date()
    const relative = formatRelative(lastRefreshedAt, now)
    const lastRefreshedLabel = !lastRefreshedAt ? 'Never' : `${relative} ago`

    return (
      <div ref={ref} className={cn(mvManagementCardVariants({ tone }), className)} {...props}>
        {/* Header: subtitle + title + accent icon */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {subtitle && (
              <div className="text-overline font-mono uppercase tracking-wider text-ia-text-tertiary mb-1">
                {subtitle}
              </div>
            )}
            <div className="text-heading-sm font-semibold text-ia-text truncate">{title}</div>
          </div>
          {icon && (
            <IconCircle
              icon={icon}
              tone={tone === 'warning' ? 'warning' : tone === 'error' ? 'error' : 'brand'}
              size="sm"
            />
          )}
        </div>

        {/* Stat row: count + status + last refresh */}
        <div className="grid grid-cols-2 gap-3 text-body-sm">
          <div>
            <div className="text-caption text-ia-text-tertiary">MVs</div>
            <div className="text-body-md font-semibold text-ia-text tabular-nums">
              {mvCount}
              {typeof failedCount === 'number' && failedCount > 0 && (
                <span className="ml-1.5 text-caption font-normal text-ia-error">
                  · {failedCount} failed
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-caption text-ia-text-tertiary">Last refresh</div>
            <div className="text-body-md font-semibold text-ia-text">{lastRefreshedLabel}</div>
          </div>
        </div>

        {/* Status row */}
        <div className="flex items-center gap-2 text-caption text-ia-text-secondary">
          <StatusDot size="sm" status={STATUS_TO_DOT[status]} aria-hidden="true" />
          <span>{STATUS_LABELS[status]}</span>
        </div>

        {/* Refresh CTA */}
        {onRefresh && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRefresh}
            loading={refreshing}
            leftIcon={RefreshCw}
            disabled={refreshing}
            className="w-full"
          >
            {refreshLabel}
          </Button>
        )}
      </div>
    )
  },
)
MVManagementCard.displayName = 'MVManagementCard'

// Re-export the LucideIcon type so consumers don't need a separate
// lucide-react dependency declaration just for the `icon` prop.
export type { LucideIcon }
